import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { GhlService } from '../../../services/ghl/ghl.service';
import { BusinessCenterOutlined } from '@mui/icons-material';

interface Option {
  id: string;
  icon: React.ReactNode;
  label: string;
}

const options: Option[] = [
  {
    id: 'sell_course',
    icon: <ShoppingBagOutlinedIcon sx={{ fontSize: 19, color: '#374151' }} />,
    label: 'Sell a course or product',
  },
  {
    id: 'live_workshops',
    icon: <SchoolOutlinedIcon sx={{ fontSize: 19, color: '#374151' }} />,
    label: 'Host live workshops or training',
  },
  {
    id: 'evergreen_funnel',
    icon: <BoltOutlinedIcon sx={{ fontSize: 19, color: '#374151' }} />,
    label: 'Build an evergreen funnel',
  },
  {
    id: 'webinars_clients',
    icon: <BusinessCenterOutlined sx={{ fontSize: 19, color: '#374151' }} />,
    label: 'Run webinars for clients',
  },
  {
    id: 'exploring',
    icon: <SearchOutlinedIcon sx={{ fontSize: 19, color: '#374151' }} />,
    label: 'Just exploring',
  },
];

interface GoalSelectionStepProps {
  email: string;
  initialSelected: string[];
  onComplete: (goals: string[]) => void;
  onSkip: () => void;
  onBack: () => void;
  ghlContactId: string;
}

export default function GoalSelectionStep({ email, initialSelected, onComplete, onSkip, onBack, ghlContactId }: GoalSelectionStepProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected || []));

  useEffect(() => {
    setSelected(new Set(initialSelected || []));
  }, [initialSelected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <Box sx={{maxWidth: 540}}>
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          color: '#111827',
          fontSize: { xs: '1.25rem', sm: '1.75rem' },
          fontWeight: 700,
          letterSpacing: '-0.01em',
          mb: 0.75,
        }}
      >
        How do you want to use EasyWebinar?
      </Typography>
      <Typography sx={{ textAlign: 'center', color: '#6B7280', fontSize: {xs: '0.82rem', sm: '0.88rem'}, mb: 3 }}>
        Help us personalize your setup (you can select multiple options).
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 3 }}>
        {options.map((opt) => {
          const isSelected = selected.has(opt.id);
          return (
            <Box
              key={opt.id}
              onClick={() => toggle(opt.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.4,
                borderRadius: '10px',
                border: isSelected ? '1.5px solid #111827' : '1.5px solid #E5E7EB',
                bgcolor: isSelected ? '#F9FAFB' : '#FFFFFF',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
                '&:hover': {
                  borderColor: isSelected ? '#111827' : '#9CA3AF',
                },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  "& svg": {
                    width: '100%',
                    height: '100%',
                  },
                }}
              >
                {opt.icon}
              </Box>

              <Typography sx={{ flex: 1, fontSize: {xs: '0.88rem', sm: '0.92rem'}, fontWeight: 500, color: '#111827' }}>
                {opt.label}
              </Typography>

              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: isSelected ? 'none' : '1.5px solid #D1D5DB',
                  bgcolor: isSelected ? '#111827' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}
              >
                {isSelected && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={selected.size === 0}
        onClick={async () => {
          onComplete(Array.from(selected));
          let ghlContactIdToUpdate = "";
          if (!ghlContactId) {
            ghlContactIdToUpdate = await GhlService.getContactByEmail(email).then((data: any) => data?.id);
          } else {
            ghlContactIdToUpdate = ghlContactId;
          }
          GhlService.updateContactFields({ contactId: ghlContactIdToUpdate, fields: [{ key: "goals", value: Array.from(selected).join(',') || '' }] });
        }}
        sx={{
          textTransform: 'none',
          borderRadius: '10px',
          py: 1,
          fontSize: '0.92rem',
          fontWeight: 600,
          color: '#FFFFFF',
          bgcolor: '#4058fb',
          boxShadow: 'none',
          '&:hover': { bgcolor: '#3442bd', boxShadow: 'none' },
          '&.Mui-disabled': {
            bgcolor: '#4058fb',
            color: '#C5C7D0',
            opacity: 0.6,
            cursor: 'not-allowed',
            boxShadow: 'none'
          },
        }}
      >
        Continue
      </Button>

      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="text"
          onClick={onBack}
          sx={{ textTransform: 'none', color: '#6066E9', fontSize: '0.9rem', fontWeight: 500 }}
        >
          Back
        </Button>
        <Button
          variant="text"
          onClick={onSkip}
          sx={{ textTransform: 'none', color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}
        >
          Skip
        </Button>
      </Box>
    </Box>
  );
}
